import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useLockBodyScroll from '../../hooks/UI/useLockBodyScroll';
import useFocusTrap from '../../hooks/UI/useFocusTrap';
import CloseModalIcon from '../../assets/icons/CloseModalIcon';
import ModalPortal from './ModalPortal';
import { Overlay, ModalContainer, CloseModalButton, CloseButtonContainer } from './Modal.styles';

interface ModalProps {
  onClose: () => void;
  isOpen: boolean;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ onClose, children, isOpen }) => {
  const { t } = useTranslation();
  const [showDrawer, setShowDrawer] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleCloseDrawer = useCallback(() => {
    setIsClosing(true);
  }, []);

  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (isClosing) {
      setShowDrawer(false);
      setTimeout(() => {
        setIsClosing(false);
        onClose();
      }, 500);
    }
  }, [isClosing, onClose]);

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setTimeout(() => {
        setShowDrawer(true);
      }, 200);
    }
  }, [isOpen]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseDrawer();
    },
    [handleCloseDrawer],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target !== e.currentTarget) return;
      handleCloseDrawer();
    },
    [handleCloseDrawer],
  );

  const focusTrapRef = useFocusTrap(true, {
    focusElementOnOpen: "div[data-component='result-info-modal']", // focus element on open
  });

  if (!isOpen) {
    return null;
  }

  return (
    <ModalPortal>
      <Overlay
        aria-label={t('vlp.vehicleResults.modalBackdrop')}
        onKeyDown={handleKeyPress}
        onClick={handleClick}
        tabIndex={0}
        data-component="result-info-modal-backdrop"
        isVisible={showDrawer}
      >
        <ModalContainer
          role="dialog"
          aria-label={t('vlp.vehicleResults.modal')}
          aria-modal="true"
          data-component="result-info-modal"
          ref={focusTrapRef}
          tabIndex={0}
          showDrawer={showDrawer}
        >
          <CloseButtonContainer>
            <CloseModalButton
              onClick={handleCloseDrawer}
              aria-label={t('vlp.vehicleResults.modalCloseButton')}
            >
              <CloseModalIcon />
            </CloseModalButton>
          </CloseButtonContainer>
          {children}
        </ModalContainer>
      </Overlay>
    </ModalPortal>
  );
};

export default Modal;
